<template>
  <div :id="containerId" :style="{ width: '100%', height: height }"></div>
</template>

<script>
export default {
  name: "HighchartsWrapper",
  props: {
    options: {
      type: Object,
      required: true,
    },
    height: {
      type: String,
      default: "400px",
    },
  },
  data() {
    return {
      containerId: `chart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      chart: null,
    };
  },
  async mounted() {
    try {
      if (typeof window.Highcharts === "undefined") {
        await this.loadHighcharts();
      }
      this.renderChart();
    } catch (e) {
      console.warn("HighchartsWrapper: chart not rendered", e);
    }
  },
  watch: {
    options: {
      handler() {
        if (this.chart) {
          this.chart.update(this.options, true);
        } else {
          this.renderChart();
        }
      },
      deep: true,
    },
  },
  beforeUnmount() {
    if (this.chart) {
      try {
        if (typeof this.chart.destroy === "function") {
          this.chart.destroy();
        }
      } catch (e) {
        console.warn("HighchartsWrapper: destroy failed", e);
      }
      this.chart = null;
    }
  },
  methods: {
    async loadHighcharts() {
      if (typeof window.Highcharts !== "undefined") {
        return;
      }
      try {
        const Highcharts = (await import("highcharts")).default;
        window.Highcharts = Highcharts;
        return;
      } catch (_e) {
        // Fallback to CDN
      }
      return new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[src*="highcharts.js"]');
        if (existingScript) {
          // Wait for it to load
          if (typeof window.Highcharts !== "undefined") {
            resolve();
            return;
          }
          const checkInterval = setInterval(() => {
            if (typeof window.Highcharts !== "undefined") {
              clearInterval(checkInterval);
              resolve();
            }
          }, 50);
          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkInterval);
            if (typeof window.Highcharts === "undefined") {
              reject(new Error("Highcharts loading timeout"));
            } else {
              resolve();
            }
          }, 10000);
          return;
        }

        const script = document.createElement("script");
        script.src = "https://code.highcharts.com/highcharts.js";
        script.onload = () => {
          // Load additional modules if needed
          const modules = [
            "https://code.highcharts.com/highcharts-more.js",
            "https://code.highcharts.com/highcharts-3d.js",
            "https://code.highcharts.com/modules/heatmap.js",
          ];

          let loaded = 0;
          if (modules.length === 0) {
            resolve();
            return;
          }

          modules.forEach((src) => {
            // Check if module script already exists
            const moduleName = src.split('/').pop();
            const existingModule = document.querySelector(`script[src*="${moduleName}"]`);
            if (existingModule) {
              loaded++;
              if (loaded === modules.length) resolve();
              return;
            }

            const modScript = document.createElement("script");
            modScript.src = src;
            modScript.onload = () => {
              loaded++;
              if (loaded === modules.length) resolve();
            };
            modScript.onerror = () => reject(new Error("Failed to load Highcharts module"));
            document.head.appendChild(modScript);
          });
        };
        script.onerror = () => reject(new Error("Failed to load Highcharts"));
        document.head.appendChild(script);
      });
    },
    renderChart() {
      if (typeof window.Highcharts === "undefined") {
        console.error("Highcharts not loaded");
        return;
      }

      this.$nextTick(() => {
        const container = document.getElementById(this.containerId);
        if (container) {
          this.chart = window.Highcharts.chart(this.containerId, this.options);
          this.$emit("chart-ready", this.chart);
        }
      });
    },
  },
};
</script>

<style scoped>
/* Chart container styles */
</style>

